import { FC, MouseEventHandler, ReactNode } from 'react';
import { Alert, AlertColor, Box, SxProps, Theme } from '@mui/material';

import PageSectionContainer from 'components/page/PageSectionContainer';

interface AccountBannerProps {
  id: string;
  severity: AlertColor;
  variant?: 'standard' | 'filled';
  /** Pinned to the bottom of the window, above everything else — for a banner whose whole
   * point is that it must stay visible no matter where the page has scrolled to. */
  fixed?: boolean;
  action?: ReactNode;
  onClick?: MouseEventHandler;
  sx?: SxProps<Theme>;
  alertSx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * The shell shared by every account-status banner: a full-width strip holding one Alert,
 * shown above the page it concerns. What varies per banner — severity, an action, whether it
 * is pinned — is left to the caller.
 */
export const AccountBanner: FC<AccountBannerProps> = ({
  id,
  severity,
  variant = 'standard',
  fixed = false,
  action,
  onClick,
  sx,
  alertSx,
  children,
}) => (
  <Box
    id={id}
    onClick={onClick}
    sx={{
      ...(fixed && {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
        boxShadow: 8,
      }),
      ...(onClick && { cursor: 'pointer' }),
      ...sx,
    }}
  >
    <PageSectionContainer sx={{ gap: 0, padding: 0 }}>
      <Alert severity={severity} variant={variant} action={action} sx={alertSx}>
        {children}
      </Alert>
    </PageSectionContainer>
  </Box>
);
